import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {InputChangeEvent} from 'util/Types';

import ChangeStudyableAssignmentPermissionWarningBanner, {
  ChangeStudyableAssignmentPermissionOriginType,
} from './studyable/ChangeStudyableAssignmentPermissionWarningBanner';
import {useCanChangeStudyableAssignmentStudiesQuery} from './studyable/useCanChangeStudyableAssignmentQuery';
import {StudyAssociation} from './StudyableDAO';
import StudySearchInput, {StudySearchControlledItemsProps, StudySearchItem} from './StudySearchInput';

export interface StudyAssociationInputProps {
  studySearchControlledItemsProps: StudySearchControlledItemsProps;
  invalidItemNames: Array<string>;
  isAutoLinkingChecked: boolean;
  isAutoLinkingDisabled: boolean;
  setIsAutoLinkingChecked: (e: InputChangeEvent) => void;
  displayPermissionWarning?: boolean;
  // The study that the item will be linked to
  autoLinkingStudy?: {
    id: string;
    name: string;
  };
}

export default function StudyAssociationInput({
  studySearchControlledItemsProps,
  invalidItemNames,
  isAutoLinkingChecked,
  isAutoLinkingDisabled,
  setIsAutoLinkingChecked,
  displayPermissionWarning,
  autoLinkingStudy,
}: StudyAssociationInputProps): JSX.Element {
  return (
    <>
      <StudySearchInput {...studySearchControlledItemsProps} shouldAllowMultiple={true} />
      <>
        {displayPermissionWarning && invalidItemNames.length > 0 && (
          <div className={css(styles.warningBanner)}>
            <ChangeStudyableAssignmentPermissionWarningBanner
              warningOriginType={ChangeStudyableAssignmentPermissionOriginType.STUDY}
              invalidStudyNames={invalidItemNames}
            />
          </div>
        )}
        {autoLinkingStudy && (
          <div className={css(styles.checkbox)}>
            <CheckboxInput
              id="study-auto-link-checkbox"
              label={
                <div>
                  Automatically link items created in this entry to <strong>{autoLinkingStudy.name}</strong>
                </div>
              }
              disabled={isAutoLinkingDisabled}
              checked={isAutoLinkingChecked}
              onChange={setIsAutoLinkingChecked}
            />
          </div>
        )}
      </>
    </>
  );
}

export function useStudyAssociationInputProps({
  initialStudySearchItems,
  displayPermissionWarning,
  autoLinkingStudy,
}: {
  initialStudySearchItems: Array<StudySearchItem>;
  displayPermissionWarning?: boolean;
  autoLinkingStudy?: {
    id: string;
    name: string;
  };
}): StudyAssociationInputProps {
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>(initialStudySearchItems);
  // Get studies missing the change studyable assignment permission
  const studyIds = studySearchItems.map((item) => item.id);
  const isOriginStudySelected = studyIds.some((studyId) => studyId === autoLinkingStudy?.id);
  const [isAutoLinkingChecked, setIsAutoLinkingChecked] = useState(isOriginStudySelected);
  const [isAutoLinkingDisabled, setIsAutoLinkingDisabled] = useState(!isOriginStudySelected);
  // Get studies missing the change studyable assignment permission
  const studyNamesToStudyablePermission = useCanChangeStudyableAssignmentStudiesQuery(studyIds);
  const invalidItemNames = Array.from(studyNamesToStudyablePermission.entries())
    .filter(([, hasPermission]) => !hasPermission)
    .map(([name]) => name);

  // We want to uncheck the auto-linking checkbox if the origin study is deselected
  useEffect(() => {
    if (!isOriginStudySelected) {
      setIsAutoLinkingChecked(false);
    }
    setIsAutoLinkingDisabled(!isOriginStudySelected || invalidItemNames.length > 0);
  }, [invalidItemNames.length, isOriginStudySelected]);

  return {
    studySearchControlledItemsProps: {selectedItems: studySearchItems, onChange: setStudySearchItems},
    invalidItemNames,
    isAutoLinkingChecked,
    isAutoLinkingDisabled,
    setIsAutoLinkingChecked: (e: InputChangeEvent) => {
      setIsAutoLinkingChecked(e.target.checked);
    },
    displayPermissionWarning,
    autoLinkingStudy,
  };
}

export function getStudyAssociations(
  studyAssociationInputProps: StudyAssociationInputProps
): Array<StudyAssociation> {
  return studyAssociationInputProps.studySearchControlledItemsProps.selectedItems.map((item) => ({
    studyId: item.id,
    isAutoLinking:
      studyAssociationInputProps.isAutoLinkingChecked &&
      item.id === studyAssociationInputProps.autoLinkingStudy?.id,
  }));
}

const styles = StyleSheet.create({
  warningBanner: {
    marginTop: 5,
  },
  checkbox: {
    marginTop: 5,
  },
});
